// ======================================
// Admin Testimonials Page Module
// ======================================

import { icons } from '../../components.js';
import { requireAuth, getUser, logout } from '../../lib/auth.js';
import { testimonialsAPI } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderAdminTestimonials() {
  if (!requireAuth(['admin', 'manager'])) {
    return '<div class="loading">Redirecting...</div>';
  }

  const user = getUser();

  const content = `
          <div class="admin-toolbar" style="display:flex;justify-content:flex-end;align-items:center;margin-bottom:var(--space-4);">
            <button class="btn btn-primary btn-sm" id="add-testimonial-btn">
              ${icons.plus || '+'} Add Testimonial
            </button>
          </div>
          
          <div class="admin-card">
            <div class="admin-table-container">
              <table class="admin-table" id="testimonials-table">
                <thead>
                  <tr>
                    <th>Avatar</th>
                    <th>Client Name</th>
                    <th>Project</th>
                    <th>Quote</th>
                    <th>Rating</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody id="testimonials-body">
                  <tr><td colspan="6" class="loading-cell">Loading testimonials...</td></tr>
                </tbody>
              </table>
            </div>
          </div>
    
    <!-- Modal -->
    <div class="modal-overlay" id="testimonial-modal" style="display:none;">
      <div class="modal-dialog" style="max-width:600px;">
        <div class="modal-header">
          <h2 class="modal-title" id="modal-title">Add New Testimonial</h2>
          <button class="modal-close" data-close-modal>&times;</button>
        </div>
        <div class="modal-body" id="modal-body">
            <!-- Form injected here -->
        </div>
      </div>
    </div>
  `;

  return renderAdminLayout('testimonials', 'Testimonials Management', content);
}

export async function initAdminTestimonials() {
  initAdminLayout();

  // Load Data
  loadTestimonials();

  // Add Button
  const addBtn = document.getElementById('add-testimonial-btn');
  if (addBtn) {
    addBtn.addEventListener('click', () => showTestimonialForm());
  }

  // Close Modals
  document.querySelectorAll('[data-close-modal]').forEach(btn => {
    btn.addEventListener('click', closeModal);
  });
}

async function loadTestimonials() {
  const tbody = document.getElementById('testimonials-body');
  if (!tbody) return;

  try {
    const response = await testimonialsAPI.list();
    const items = Array.isArray(response) ? response : (response.data || []);

    if (items.length === 0) {
      tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:2rem;">No testimonials found. Add one!</td></tr>';
      return;
    }

    tbody.innerHTML = items.map(item => `
      <tr>
        <td>
            <img src="${item.avatar || 'https://via.placeholder.com/50'}" alt="${item.name}" style="width:40px;height:40px;border-radius:50%;object-fit:cover;">
        </td>
        <td>
            <div style="font-weight:600;">${item.name}</div>
            <div style="font-size:12px;color:var(--color-text-muted);">${item.role || 'Client'}</div>
        </td>
        <td>${item.project || '-'}</td>
        <td>
            <div style="font-size:12px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;max-width:250px;">
                "${item.quote}"
            </div>
        </td>
         <td>
            <div style="color: gold;">${'★'.repeat(item.rating || 5)}</div>
        </td>
        <td>
          <div class="action-buttons">
            <button class="btn-icon" onclick="window.editTestimonial(${item.id})" title="Edit">${icons.edit || '✎'}</button>
            <button class="btn-icon delete" onclick="window.deleteTestimonial(${item.id})" title="Delete">${icons.trash || '🗑️'}</button>
          </div>
        </td>
      </tr>
    `).join('');
  } catch (error) {
    console.error('Failed to load testimonials:', error);
    tbody.innerHTML = `<tr><td colspan="6" class="error-cell">Failed to load: ${error.message}</td></tr>`;
  }
}

function showTestimonialForm(data = null) {
  const modal = document.getElementById('testimonial-modal');
  const modalBody = document.getElementById('modal-body');
  const modalTitle = document.getElementById('modal-title');

  if (!modal || !modalBody) return;

  const isEdit = !!data;
  modalTitle.textContent = isEdit ? 'Edit Testimonial' : 'Add New Testimonial';
  modal.style.display = 'flex';

  modalBody.innerHTML = `
    <form id="testimonial-form">
      <div class="form-grid">
        <div class="form-group full-width">
          <label class="form-label">Client Name *</label>
          <input type="text" name="name" class="form-input" required value="${data?.name || ''}">
        </div>
        <div class="form-group">
          <label class="form-label">Project Name</label>
          <input type="text" name="project" class="form-input" value="${data?.project || ''}" placeholder="e.g. Villa in OMR">
        </div>
        <div class="form-group">
          <label class="form-label">Rating (1-5)</label>
          <select name="rating" class="form-select">
            <option value="5" ${data?.rating == 5 ? 'selected' : ''}>5 Stars</option>
            <option value="4" ${data?.rating == 4 ? 'selected' : ''}>4 Stars</option>
            <option value="3" ${data?.rating == 3 ? 'selected' : ''}>3 Stars</option>
            <option value="2" ${data?.rating == 2 ? 'selected' : ''}>2 Stars</option>
            <option value="1" ${data?.rating == 1 ? 'selected' : ''}>1 Star</option>
          </select>
        </div>
        <div class="form-group full-width">
          <label class="form-label">Avatar URL</label>
          <input type="url" name="avatar" class="form-input" value="${data?.avatar || ''}" placeholder="https://...">
        </div>
        <div class="form-group full-width">
          <label class="form-label">Quote *</label>
          <textarea name="quote" class="form-textarea" rows="3" required>${data?.quote || ''}</textarea>
        </div>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" onclick="window.closeModal()">Cancel</button>
        <button type="submit" class="btn btn-primary">${isEdit ? 'Update' : 'Create'}</button>
      </div>
    </form>
  `;

  // Submit Handler
  const form = document.getElementById('testimonial-form');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('button[type="submit"]');
    btn.disabled = true;
    btn.textContent = 'Saving...';

    const formData = new FormData(form);
    const submitData = Object.fromEntries(formData.entries());

    try {
      if (isEdit) {
        await testimonialsAPI.update(data.id, submitData);
      } else {
        await testimonialsAPI.create(submitData);
      }
      closeModal();
      loadTestimonials();
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to save');
      btn.disabled = false;
      btn.textContent = isEdit ? 'Update' : 'Create';
    }
  });
}

export function closeModal() {
  const modal = document.getElementById('testimonial-modal');
  if (modal) modal.style.display = 'none';
}

// Global functions for inline usage
window.closeModal = closeModal;
window.editTestimonial = async (id) => {
  try {
    // Just fetch from list if possible or get by ID if API supports
    // testimonialsAPI.get(id) might not be implemented, let's use list and find
    // Wait, I implemented get(id) in api.js? No, just list/create/update/delete usually for simple resources.
    // Let's check api.js: testimonialsAPI has update(id, data). It does not have get(id).
    // I can fetch list again or pass full object? 
    // Better to just fetch list and find. Or cleaner: implement get(id) in API if needed.
    // But for now, let's just find in DOM or fetch list.
    const response = await testimonialsAPI.list();
    const items = Array.isArray(response) ? response : (response.data || []);
    const item = items.find(i => i.id == id);
    if (item) showTestimonialForm(item);
  } catch (e) {
    alert('Failed to load details');
  }
};
window.deleteTestimonial = async (id) => {
  if (confirm('Are you sure you want to delete this testimonial?')) {
    try {
      await testimonialsAPI.delete(id);
      loadTestimonials();
    } catch (e) {
      alert('Failed to delete');
    }
  }
};
